import axios from "axios";
import AuthenticationFailureHandler from "../../utils/AuthenticationFailureHandler";
import { getHeaders } from "../../utils/AuthorizationUtil";
import {
  RESERVATION_BASE_URL,
} from "../../utils/EmpUtil";
import { is401, is400 } from "../../errors/ErrorConstants";

export default class CustomerReservationsClient {
  static baseUrl = RESERVATION_BASE_URL;

  static getCustomerReservations(
    dealerCommonId,
    currentPage,
    lang,
    sortBy,
  ) {
    // Ensure dealerCommonId is valid before making API call
    if (
      !dealerCommonId ||
      dealerCommonId === "null" ||
      dealerCommonId === "undefined"
    ) {
      return Promise.reject(new Error("Invalid dealerCommonId provided"));
    }

    const url = `${this.baseUrl}/dealer/customer-reservations`;
    dealerCommonId = dealerCommonId.split("%7C").join("|");
    return axios
      .post(
        url,
        {
          commonIds: dealerCommonId,
          currentPage: currentPage,
          sort: sortBy,
          lang: lang,
        },
        getHeaders(),
      )
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static getCustomerOrders(dealerCommonId, tab, lang) {
    let statusList = [];
    let orderType = null;
    let sort = null;
    let purchaseRequestOrders = false;

    if (tab === "orders_requiring_attention") {
      statusList = ["SUBMITTED"];
      orderType = "Order";
      sort = "byDealerActionReqDateAsc";
    } else if (tab === "reservations_submitted") {
      orderType = "Reservation";
      statusList = ["CREATED", "CANCELLED", "AMENDED"];
    } else if (tab === "orders_submitted") {
      orderType = "Order";
      statusList = [
        "REJECTED",
        "CANCELLED",
        "CONFIRMED",
        "CONTRACTED",
        "CONVERTED",
        "ORDERED",
        "SOLD",
      ];
      sort = "byOrderConfirmedDateDesc";
    } else if (tab === "purchase_request") {
      orderType = "Order";
      purchaseRequestOrders = true;
    }
    dealerCommonId = dealerCommonId.split("%7C").join("|");
    statusList = statusList.join(",");
    const url = `${this.baseUrl}/dealer/customer-reservations`;
    return axios
      .post(
        url,
        {
          commonIds: dealerCommonId,
          status: statusList,
          currentPage: 0,
          orderType: orderType,
          sort: sort,
          lang: lang,
          purchaseRequestOrders: purchaseRequestOrders,
        },
        getHeaders(),
      )
      .then((response) => {
        return response.data.reservations;
      })
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }

        return Promise.reject(error);
      });
  }

  static updateOrder(
    dealerCommonId,
    dealerActionInfo,
    dealerUserId,
    returnError,
  ) {
    dealerCommonId = dealerCommonId.split("|").join("%7C");
    const url = `${this.baseUrl}/dealer/${dealerCommonId}/customer-orders?dealerUserId=${dealerUserId}`;

    return axios
      .post(url, dealerActionInfo, getHeaders())
      .then(() => {
        return Promise.resolve();
      })
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        if (returnError && is400(error)) {
          // will be handling this 400 error in the calling method
          return "400";
        }
        return Promise.reject(error);
      });
  }

  static getCancelOrderReasons(
    lang,
    purchaseRequest = false,
    consentReasons = false,
    reservationReasons = false,
    vehicleHoldReasons = false,
  ) {
    let url = `${this.baseUrl}/dealer/cancelOrderReasons?lang=${lang}`;
    if (purchaseRequest) {
      url = url + `&purchaseRequest=${purchaseRequest}`;
    }
    if (consentReasons) {
      url = url + `&consentReasons=true`;
    }
    if (reservationReasons) {
      url = url + `&reservationReasons=true`;
    }
    if (vehicleHoldReasons) {
      url = url + `&vehicleHoldReasons=true`;
    }
    return axios
      .get(url, getHeaders())
      .then((response) => response.data)
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static async getCustomerOrderDetails(
    user,
    orderId,
    commonId,
    isCrc,
    lang,
    market,
  ) {
    let url = `${this.baseUrl}/dealer/customer-reservations/${orderId}?lang=${lang}`;
    url = url + (isCrc ? "&market=" + market : "&dealerId=" + commonId);
    return await axios
      .get(encodeURI(url), getHeaders())
      .then((response) => response.data)
      .catch(async (error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static async searchCustomerReservations(
    searchQuery,
    user,
    lang,
    commonIds,
    market
  ) {
    let url = `${this.baseUrl}/dealer/customer-reservations/search?lang=${lang}`;

    if (market) {
      url = url + `&market=${market}`;
    }

    let commonIdList = commonIds ? commonIds.split(",").filter((id) => id && id.trim() !== "") : [];
    const cleanCommonIds = commonIdList.map((id) => id.split("%7C").join("|"));

    const headers = {
      headers: {
        ...getHeaders().headers,
        ...(searchQuery && { searchQuery: searchQuery }),
        ...(cleanCommonIds.length && { commonIds: cleanCommonIds.join(",") }),
      },
    };

    return axios
      .get(url, headers)
      .then((response) => response.data)
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static async saveValuation(valuationRequest) {
    const url = `${this.baseUrl}/dealer/customer-reservations/trade-in`;

    return axios
      .put(
        url,
        {
          expiryDate: valuationRequest.expiryDate,
          valuationAmount: valuationRequest.valuationAmount,
          appraisalId: valuationRequest.appraisalId,
          orderId: valuationRequest.orderId,
        },
        getHeaders(),
      )
      .then((response) => response.data)
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }
}
